<template>
  <ts-modal
    ref="modal"
    modal-title="Add trainer to event"
    modal-class="modal-form candidate-event-modal"
    :keyboard="false"
  >
    <template #modal-content>
      <div ref="modalBody">
        <alert
          type="danger"
          :message="errorMessage"
          @dismiss="clearError"
        />

        <div class="add-event">
          <div class="row">
            <div class="col">
              <div class="mb-2">
                <label class="form-label">Event <span class="text-danger">*</span></label>
                <input
                  :value="selectedEvent.eventName"
                  type="text"
                  readonly
                  class="form-control"
                >
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <div class="mb-2">
                <label class="form-label">Options</label>
                <div class="form-check">
                  <input
                    id="customCheck1"
                    v-model="selectedEvent.isStaff"
                    type="checkbox"
                    class="form-check-input"
                  >
                  <label
                    class="form-check-label mb-1"
                    for="customCheck1"
                  >
                    EJTN staff
                  </label>
                  <div class="small text-muted">Staff members are not included in event surveys (participants can't rate
                    them)
                  </div>
                </div>
                <div class="form-check">
                  <input
                    id="customCheck2"
                    v-model="selectedEvent.isActivityCoordinator"
                    type="checkbox"
                    class="form-check-input"
                  >
                  <label
                    class="form-check-label mb-1"
                    for="customCheck2"
                  >
                    Activity coordinator
                  </label>
                  <div class="small text-muted">Activity coordinators are not included in event surveys (participants
                    can't rate them)
                  </div>
                </div>
              </div>
            </div>
          </div>
          <loading-indicator
            v-if="isLoading"
          />
        </div>
      </div>
    </template>
    <template #modal-footer>
      <button
        type="button"
        class="btn btn-sm btn-secondary"
        data-bs-dismiss="modal"
      >
        Close
      </button>
      <button
        type="button"
        :disabled="!canSave"
        class="btn btn-sm btn-primary"
        @click="updateEvent"
      >
        Ok
      </button>
    </template>
  </ts-modal>
</template>

<script setup lang="ts">
import {ref, computed, useTemplateRef} from 'vue'
import TsModal from './ts-modal.vue'
import Alert from './alert.vue'
import axios from 'axios'
import Rails from '@rails/ujs'
import LoadingIndicator from './loading-indicator.vue'

export interface TrainerEvent {
  eventId: number
  eventName: string
  candidateId: number
  isActivityCoordinator: boolean
  isStaff: boolean
}

const props = defineProps({
  saveUrl: {type: String, required: true}
})

// Template refs
const modal = useTemplateRef<InstanceType<typeof TsModal>>("modal")
const modalBody = useTemplateRef<HTMLElement>("modalBody")

// Reactive state
const errorMessage = ref<string | undefined>(undefined)
const isLoading = ref(false)
const selectedEvent = ref<TrainerEvent>({
  eventId: 0,
  eventName: "",
  candidateId: 0,
  isStaff: false,
  isActivityCoordinator: false,
})

const canSave = computed((): boolean => {
  return !isLoading.value
})

// Methods
const clearError = (): void => {
  errorMessage.value = undefined
}

const showModal = (trainerEvent: TrainerEvent): void => {
  clearError()
  resetEvent()

  selectedEvent.value = {
    eventId: trainerEvent.eventId,
    eventName: trainerEvent.eventName,
    candidateId: trainerEvent.candidateId,
    isStaff: trainerEvent.isStaff,
    isActivityCoordinator: trainerEvent.isActivityCoordinator,
  }

  modal.value?.show()
}

const resetEvent = (): void => {
  Object.assign(selectedEvent, {
    id: null,
    eventId: null,
    eventName: null,
    rm: false,
    modifiedAtStr: null,
    candidateId: null,
    isStaff: false,
    isActivityCoordinator: false,
  })
}

const updateEvent = (): void => {
  clearError()

  isLoading.value = true
  axios.post(props.saveUrl, {
    event_candidate: {
      candidate_id: selectedEvent.value.candidateId,
      is_staff: selectedEvent.value.isStaff ? '1' : '0',
      is_activity_coordinator: selectedEvent.value.isActivityCoordinator ? '1' : '0'
    }
  }, {
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': Rails.csrfToken(),
      'Accept': 'application/json'
    }
  })
    .then((response) => {
      if (typeof response.data.error === 'string') {
        errorMessage.value = response.data.error
        isLoading.value = false
      } else {
        // eslint-disable-next-line no-self-assign
        window.location.href = window.location.href
      }
    })
    .catch((error) => {
      errorMessage.value = error.message
      isLoading.value = false
    })
}

defineExpose({
  showModal,
})
</script>
